import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Calendar,
  FlaskConical,
  TestTube,
  DollarSign,
  Check,
  Loader2,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react';
import { getPanelsFromLabService, LabPanel } from '@/services/labService';

interface FollowupLabStepProps {
  followupDays: string;
  onFollowupDaysChange: (value: string) => void;
  orderFollowupLab: boolean;
  onOrderFollowupLabChange: (value: boolean) => void;
  selectedTest: LabPanel | null;
  onSelectedTestChange: (test: LabPanel | null) => void;
  onBack: () => void;
}

const FollowupLabStep: React.FC<FollowupLabStepProps> = ({
  followupDays,
  onFollowupDaysChange,
  orderFollowupLab,
  onOrderFollowupLabChange,
  selectedTest,
  onSelectedTestChange,
  onBack,
}) => {
  const [panels, setPanels] = useState<LabPanel[]>([]);
  const [loadingTests, setLoadingTests] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (orderFollowupLab && panels.length === 0) {
      loadPanels();
    }
  }, [orderFollowupLab]);

  const loadPanels = async () => {
    try {
      setLoadingTests(true);
      setLoadError(null);
      const fetchedPanels = await getPanelsFromLabService();
      setPanels(fetchedPanels);
      if (fetchedPanels.length === 0) {
        setLoadError('No lab panels available. Please contact your administrator.');
      }
    } catch (error) {
      console.error('Failed to load lab panels:', error);
      setLoadError('Failed to load lab panels. Please try again or contact your administrator.');
    } finally {
      setLoadingTests(false);
    }
  };

  // Format treatment area name for display
  const formatTreatmentArea = (area: string): string => {
    const labels: Record<string, string> = {
      NIMCORE_MEN: "Men's Health",
      NIMCORE_WOMEN: "Women's Health",
      HRT_MEN: "Men's Health",
      HRT_WOMEN: "Women's Health",
      WEIGHT_LOSS: 'Weight Loss',
      HAIR_LOSS: 'Hair Loss',
      GENERAL: 'General',
      OTHER: 'Other',
    };
    return labels[area] || area.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const sortPanels = (a: LabPanel, b: LabPanel) => {
    const aDefault = a.isDefault || a.is_default ? 1 : 0;
    const bDefault = b.isDefault || b.is_default ? 1 : 0;
    if (aDefault !== bDefault) return bDefault - aDefault;

    const aOrder = a.display_order ?? 9999;
    const bOrder = b.display_order ?? 9999;
    if (aOrder !== bOrder) return aOrder - bOrder;

    return a.name.localeCompare(b.name);
  };

  // Group panels by treatment area
  const groupedPanels = panels.reduce((acc, panel) => {
    const groupKey = panel.treatmentArea || panel.treatment_area || 'OTHER';
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(panel);
    return acc;
  }, {} as Record<string, LabPanel[]>);

  Object.values(groupedPanels).forEach(areaPanels => {
    areaPanels.sort(sortPanels);
  });

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Review
      </button>

      {/* Section Title */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
          <FlaskConical className="h-5 w-5 text-purple-600" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">Follow-up Lab</h3>
          <p className="text-sm text-slate-500">Configure follow-up lab work for this prescription</p>
        </div>
      </div>

      {/* Follow-up Days */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
          <Calendar className="h-4 w-4 text-slate-500" />
          Follow-up Schedule
        </Label>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600">Follow up after</span>
          <Input
            type="number"
            min="1"
            max="365"
            value={followupDays}
            onChange={(e) => onFollowupDaysChange(e.target.value)}
            placeholder="30"
            className="w-24"
          />
          <span className="text-sm text-slate-600">days</span>
        </div>
      </div>

      <div className="border-t border-slate-200 my-4" />

      {/* Order Follow-up Lab Checkbox */}
      <div className="flex items-center space-x-3">
        <Checkbox
          id="orderFollowupLab"
          checked={orderFollowupLab}
          onCheckedChange={(checked) => {
            onOrderFollowupLabChange(checked as boolean);
            if (!checked) {
              onSelectedTestChange(null);
            }
          }}
        />
        <Label
          htmlFor="orderFollowupLab"
          className="text-sm font-medium text-slate-700 cursor-pointer flex items-center gap-2"
        >
          <FlaskConical className="h-4 w-4 text-slate-500" />
          Order Follow-up Lab
        </Label>
      </div>

      {/* Lab Test Selection - shown when checkbox is checked */}
      {orderFollowupLab && (
        <div className="space-y-4 mt-4">
          <Label className="text-sm font-medium text-slate-700">
            Select Lab Test
          </Label>

          {loadingTests ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : loadError || panels.length === 0 ? (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700">
                {loadError || 'No lab panels available. Please contact your administrator.'}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
              {Object.entries(groupedPanels).map(([treatmentArea, areaPanels]) => (
                <div key={treatmentArea}>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">
                    {formatTreatmentArea(treatmentArea)}
                  </h4>
                  <div className="space-y-2">
                    {areaPanels.map(panel => (
                      <Card
                        key={panel.id || panel.panelId}
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          selectedTest?.id === panel.id || selectedTest?.panelId === panel.panelId
                            ? 'ring-2 ring-purple-500 border-purple-500'
                            : 'border-slate-200'
                        }`}
                        onClick={() => onSelectedTestChange(panel)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <TestTube className="h-4 w-4 text-purple-600" />
                                <span className="font-medium text-slate-900">{panel.name}</span>
                                {panel.isDefault || panel.is_default ? (
                                  <Badge variant="outline" className="text-xs">
                                    Default
                                  </Badge>
                                ) : null}
                              </div>
                              {panel.description && (
                                <p className="text-sm text-slate-500 mt-1">
                                  {panel.description}
                                </p>
                              )}
                              {panel.tests && panel.tests.length > 0 && (
                                <div className="flex flex-nowrap gap-1 mt-2 overflow-x-auto">
                                  {panel.tests.slice(0, 6).map((test, idx) => (
                                    <Badge
                                      key={test.id || idx}
                                      variant="secondary"
                                      className="text-xs whitespace-nowrap flex-shrink-0"
                                    >
                                      {test.name}
                                    </Badge>
                                  ))}
                                  {panel.tests.length > 6 && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Badge variant="outline" className="text-xs whitespace-nowrap flex-shrink-0 cursor-help">
                                          +{panel.tests.length - 6} more
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-xs">
                                        <div className="space-y-1">
                                          <p className="font-semibold text-xs mb-1">Additional tests:</p>
                                          <ul className="list-disc list-inside space-y-0.5">
                                            {panel.tests.slice(6).map((test, idx) => (
                                              <li key={test.id || idx} className="text-xs">
                                                {test.name}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              {panel.price != null && panel.price > 0 && (
                                <div className="flex items-center gap-1 text-green-600 font-semibold">
                                  <DollarSign className="h-4 w-4" />
                                  {Number(panel.price).toFixed(0)}
                                </div>
                              )}
                              {(selectedTest?.id === panel.id || selectedTest?.panelId === panel.panelId) && (
                                <Check className="h-5 w-5 text-purple-600 mt-2" />
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info Alert */}
      <Alert className="border-purple-200 bg-purple-50">
        <FlaskConical className="h-4 w-4 text-purple-600" />
        <AlertDescription className="text-purple-700 text-sm">
          Follow-up lab information will be sent along with the prescription data
          to enable automated lab ordering workflows.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default FollowupLabStep;
